import { axios } from '@/api/axios.config'
import { v2Api } from '@/utils/global/api'
import { CASE_IMAGE_JOB_STATUS_KEY } from '@messenger/consts'

/**
 * Cap on each status poll (case image jobs and variant jobs alike) so a
 * half-dead backend (connection accepted but no response) surfaces as a
 * retryable timeout instead of hanging the upload or review forever. The
 * shared axios instance has no default timeout.
 */
export const JOB_STATUS_REQUEST_TIMEOUT_MS = 10000

type CaseImageJobStatus =
  (typeof CASE_IMAGE_JOB_STATUS_KEY)[keyof typeof CASE_IMAGE_JOB_STATUS_KEY]

type PresignedUrlResponse = {
  url: string
  s3Key: string
}

type ProcessImageResponse = {
  jobId: string
}

type JobStatusResponse = {
  jobId: string
  status: CaseImageJobStatus | 'NOT_FOUND'
  errorMessage?: string | null
  caseImage?: {
    originalUrl: string
    mioUrl?: string
  } | null
}

type DeleteCaseImageResponse = {
  success: boolean
}

type MoveToFrontResponse = {
  success: boolean
}

// --- Single image endpoints ---

export const getPresignedUploadUrl = async (
  caseId: string,
  contentType: string,
): Promise<PresignedUrlResponse> => {
  const response = await axios.post<PresignedUrlResponse>(
    `${v2Api}case-images/presigned-url`,
    { caseId, contentType },
  )
  return response.data
}

export const processImage = async (
  caseId: string,
  s3Key: string,
  originalFileName?: string,
): Promise<ProcessImageResponse> => {
  const response = await axios.post<ProcessImageResponse>(
    `${v2Api}case-images/process`,
    { caseId, s3Key, originalFileName },
  )
  return response.data
}

export const getJobStatus = async (
  jobId: string,
): Promise<JobStatusResponse> => {
  const response = await axios.get<JobStatusResponse>(
    `${v2Api}case-images/status/${jobId}`,
    { timeout: JOB_STATUS_REQUEST_TIMEOUT_MS },
  )
  return response.data
}

// --- Batch endpoints ---

export const getPresignedUploadUrls = async (
  caseId: string,
  contentTypes: string[],
): Promise<{ uploads: PresignedUrlResponse[] }> => {
  const response = await axios.post<{ uploads: PresignedUrlResponse[] }>(
    `${v2Api}case-images/presigned-urls`,
    { caseId, contentTypes },
  )
  return response.data
}

export const processImageBatch = async (
  caseId: string,
  items: Array<{ s3Key: string; originalFileName?: string }>,
): Promise<{ jobIds: string[] }> => {
  const response = await axios.post<{ jobIds: string[] }>(
    `${v2Api}case-images/process-batch`,
    { caseId, items },
  )
  return response.data
}

export const getBatchJobStatus = async (
  jobIds: string[],
): Promise<{ jobs: JobStatusResponse[] }> => {
  const response = await axios.post<{ jobs: JobStatusResponse[] }>(
    `${v2Api}case-images/status-batch`,
    { jobIds },
    { timeout: JOB_STATUS_REQUEST_TIMEOUT_MS },
  )
  return response.data
}

// --- List endpoint ---

export type CaseImageRecord = {
  id: string
  originalUrl: string
  mioUrl: string | null
  displayUrl: string
  default: boolean
  sortOrder: number
  uploadSource: string
}

/**
 * Cap on the case image list request. Opening a case's details (the
 * dashboard, the print package) waits for the list, and so does the
 * editor's load, so a backend that accepts the connection and never answers
 * must surface as a failure, which every caller already handles, instead of
 * an endless wait. It is longer than the status poll's cap because the list
 * is read once per open, not polled.
 */
export const CASE_IMAGE_LIST_REQUEST_TIMEOUT_MS = 20000

export const getCaseImageList = async (
  caseId: string,
): Promise<CaseImageRecord[]> => {
  const response = await axios.get<{ images: CaseImageRecord[] }>(
    `${v2Api}case-images/list/${caseId}`,
    { timeout: CASE_IMAGE_LIST_REQUEST_TIMEOUT_MS },
  )
  return response.data.images
}

// --- Other endpoints ---

export const deleteCaseImageV2 = async (
  caseId: string,
  imageUrl: string,
): Promise<DeleteCaseImageResponse> => {
  const response = await axios.delete<DeleteCaseImageResponse>(
    `${v2Api}case-images/${caseId}`,
    { data: { imageUrl } },
  )
  return response.data
}

export const moveImageToFront = async (
  caseId: string,
  fromIndex: number,
): Promise<MoveToFrontResponse> => {
  const response = await axios.post<MoveToFrontResponse>(
    `${v2Api}case-images/move-to-front`,
    { caseId, fromIndex },
  )
  return response.data
}
